import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        if (this.isDrawer) {
          this.reconcileFromSectionHTML(parsedResponseText.sections[this.sectionId]);
        } else {
          morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], { mode: 'full' });
        }

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      if (this.isDrawer) {
        this.reconcileFromSectionHTML(cartItemsHtml);
      } else {
        morphSection(this.sectionId, cartItemsHtml, { mode: 'full' });
      }

      // Update button states for all cart quantity selectors after replacement.
      this.#updateCartQuantitySelectorButtonStates();
    } else if (this.isDrawer) {
      // A cart response without rendered sections is not safe to apply to a
      // drawer line list. Ask the drawer's reconciliation layer for a fresh,
      // authoritative server render instead of morphing stale rows by position.
      document.dispatchEvent(new CustomEvent('ysn:cart-refresh-request'));
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false, mode: 'full' });
    }
  };

  /**
   * Reconciles an authoritative server-rendered cart without replacing the
   * premium drawer shell. Only the line-item data, optional upsells/add-on,
   * totals and count bubble are updated. Header spacing, tracking content,
   * guarantee/timer blocks, sticky layout and all editor-controlled styling
   * remain untouched.
   *
   * @param {string} sectionHTML
   * @returns {boolean} Whether the server markup was applied.
   */
  reconcileFromSectionHTML(sectionHTML) {
    if (!sectionHTML) return false;

    const parsed = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const normalizedSectionId = this.sectionId.replace(/^shopify-section-/, '');
    const renderedSection = parsed.getElementById(`shopify-section-${normalizedSectionId}`);
    const freshComponent = renderedSection?.querySelector('cart-drawer-component cart-items-component[data-drawer]');

    if (!(freshComponent instanceof HTMLElement)) return false;

    const currentHasCart = this.querySelector('.cart-form') instanceof HTMLElement;
    const freshHasCart = freshComponent.querySelector('.cart-form') instanceof HTMLElement;

    // Empty/non-empty transitions require the complete component contents, but
    // the existing drawer element and its presentation variables are preserved.
    if (currentHasCart !== freshHasCart) {
      const nodes = Array.from(freshComponent.childNodes, (node) => node.cloneNode(true));
      this.replaceChildren(...nodes);
      this.updatedCallback?.();

      // The drawer may already be open when the first item is inserted. In that
      // case its dialog still carries the empty-cart class, whose CSS adds the
      // extra 28px content padding visible immediately after Add to cart. Keep
      // the original drawer DOM/design and only synchronize its state classes.
      const drawer = this.closest('cart-drawer-component');
      if (drawer && typeof drawer.refreshPresentationState === 'function') {
        drawer.refreshPresentationState();
      }

      document.dispatchEvent(new CustomEvent('ysn:cart-reconciled-dom'));
      return true;
    }

    if (!freshHasCart) {
      // Both states are empty; no visual or structural replacement is needed.
      const drawer = this.closest('cart-drawer-component');
      if (drawer && typeof drawer.refreshPresentationState === 'function') {
        drawer.refreshPresentationState();
      }
      return true;
    }

    const itemsScroller = this.closest('cart-drawer-component')?.querySelector('.cart-drawer__items');
    const previousScrollTop = itemsScroller instanceof HTMLElement ? itemsScroller.scrollTop : 0;

    const replaceRequired = (selector) => {
      const current = this.querySelector(selector);
      const fresh = freshComponent.querySelector(selector);
      if (!(current instanceof HTMLElement) || !(fresh instanceof HTMLElement)) return false;
      current.replaceWith(fresh.cloneNode(true));
      return true;
    };

    // The product wrapper is the critical authoritative region. Replacing it
    // prevents stale line numbers, missing products and duplicated rows while
    // leaving the surrounding drawer design exactly as configured.
    if (!replaceRequired('.cart-items__wrapper')) return false;

    // Update the count shown in the drawer heading without replacing the header.
    const currentBubble = this.querySelector('.cart-drawer__heading .cart-bubble');
    const freshBubble = freshComponent.querySelector('.cart-drawer__heading .cart-bubble');
    if (currentBubble instanceof HTMLElement && freshBubble instanceof HTMLElement) {
      currentBubble.replaceWith(freshBubble.cloneNode(true));
    }

    this.#syncOptionalRegion(freshComponent, '[data-ysn-cart-upsells]', '.cart-drawer__items', 'append');
    this.#syncOptionalRegion(freshComponent, '[data-ysn-cart-addon]', '.ysn-cart-drawer-summary', 'prepend');
    replaceRequired('[data-ysn-cart-footer]');

    this.updatedCallback?.();

    if (itemsScroller instanceof HTMLElement) {
      requestAnimationFrame(() => {
        itemsScroller.scrollTop = Math.min(previousScrollTop, itemsScroller.scrollHeight);
      });
    }

    document.dispatchEvent(new CustomEvent('ysn:cart-reconciled-dom'));
    return true;
  }

  /**
   * Synchronizes an optional cart region while preserving its parent layout.
   * @param {HTMLElement} freshComponent
   * @param {string} selector
   * @param {string} parentSelector
   * @param {'append' | 'prepend'} placement
   */
  #syncOptionalRegion(freshComponent, selector, parentSelector, placement) {
    const current = this.querySelector(selector);
    const fresh = freshComponent.querySelector(selector);

    if (current instanceof HTMLElement && fresh instanceof HTMLElement) {
      current.replaceWith(fresh.cloneNode(true));
      return;
    }

    if (current instanceof HTMLElement && !(fresh instanceof HTMLElement)) {
      current.remove();
      return;
    }

    if (!(current instanceof HTMLElement) && fresh instanceof HTMLElement) {
      const parent = this.querySelector(parentSelector);
      if (!(parent instanceof HTMLElement)) return;
      const clone = fresh.cloneNode(true);
      if (placement === 'prepend') {
        const footer = parent.querySelector('[data-ysn-cart-footer]');
        if (footer) parent.insertBefore(clone, footer);
        else parent.prepend(clone);
      } else {
        parent.append(clone);
      }
    }
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
